export default {
  title: "AI Employees",
  caption: "Manage AI-powered employee personas",
  // Friendly, class-keyed copy for run/chat failures. Mirrors the backend
  // service.FriendlyErrorMessage so every surface (chat notice, run detail, run
  // list) speaks the same language. Keyed by ClassifyError bucket.
  errors: {
    rate_limit: {
      title: "Rate limited",
      message: "The AI service is busy right now. Try again in a moment.",
      suggestion:
        "Wait a minute and try again. If it keeps happening, lower the run frequency or upgrade the provider plan.",
    },
    auth_failed: {
      title: "Authentication failed",
      message:
        "Couldn't authenticate with the AI service. Please contact support.",
      suggestion:
        "Re-authenticate the connected service under Resources — its key or token is likely expired or revoked.",
    },
    timeout: {
      title: "Timed out",
      message: "The AI service didn't respond in time. Try again.",
      suggestion:
        "Try again, or simplify the request. If one step always hangs, open the run to diagnose it.",
    },
    network: {
      title: "Network error",
      message: "Couldn't reach the AI service. Try again.",
      suggestion:
        "Usually transient — try again. If it persists, check the service is reachable.",
    },
    not_found: {
      title: "Model unavailable",
      message:
        "The requested AI model isn't available. Please contact support.",
      suggestion:
        "Check the selected model and that any referenced resources still exist.",
    },
    context_overflow: {
      title: "Conversation too long",
      message:
        "This conversation is too long for the model. Start a new thread.",
      suggestion:
        "Start a new conversation, or switch this employee to a larger-context model.",
    },
    max_iterations: {
      title: "Reached the step limit",
      message: "The run used its full step budget without finishing.",
      suggestion:
        "Narrow the request, or raise Max steps. A tool loop is the common cause — open the run to see which step repeats.",
    },
    guardrail: {
      title: "Stopped by a guardrail",
      message:
        "A safety guardrail blocked this action. Try a different approach.",
      suggestion:
        "Try a different approach. If it was a false positive, ask an admin to adjust the guardrail.",
    },
    insufficient_credits: {
      title: "Out of credits",
      message:
        "You're out of credits for AI employee runs. Top up your balance or contact your administrator.",
      suggestion:
        "Top up the workspace balance, or ask an administrator to add credits.",
    },
    missing_config: {
      title: "Setup incomplete",
      message:
        "This needs a credential or setting that isn't configured yet. Ask an admin to finish setting up the integration.",
      suggestion:
        "Ask an admin to finish the integration — add the missing resource or secret under Resources.",
    },
    quota_exceeded: {
      title: "Daily quota reached",
      message:
        "The daily search quota for this workspace was reached. It resets tomorrow.",
      suggestion:
        "The daily quota resets tomorrow. Ask an admin to raise the cap if you regularly need more.",
    },
    tool_schema: {
      title: "Tool configuration issue",
      message:
        "A tool couldn't be prepared for this model. This is a configuration issue — please contact your administrator.",
      suggestion:
        "A tool isn't compatible with this model. Ask an admin to switch the model or fix the tool.",
    },
    qc_blocked: {
      title: "Held for review",
      message:
        "This response was held back because it didn't pass a quality or safety check.",
      suggestion:
        "Review the response, or ask an admin to adjust the employee's QC policy.",
    },
    other: {
      title: "Something went wrong",
      message:
        "Something went wrong while generating the response. Please try again.",
      suggestion:
        "Try again. If it keeps failing, open the run to diagnose the failing step, or contact support.",
    },
    showDetail: "Show technical detail",
    hideDetail: "Hide technical detail",
    retry: "Try again",
    suggestedFix: "Suggested fix",
  },
  list: {
    title: "AI Employees",
    empty: "No AI employees yet. Create one to get started.",
    emptyInactive: "No inactive AI employees.",
    create: "New Employee",
    editSchedule: "Edit schedule",
    deleteSchedule: "Delete schedule",
    runNow: "Run now",
    viewRuns: "View run history",
    viewLastRun: "Open this run",
    delete: "Delete employee",
    deleted: "Employee deleted",
    deleteFailed: "Could not delete employee",
    deleteConfirmTitle: 'Delete "{name}"?',
    deleteConfirmBody:
      "This permanently deletes the employee and all of its sub-agents, tools, runs, conversations, memories and version history. This cannot be undone.",
  },
  versions: {
    snapshot: "Snapshot current",
    snapshotted: "Snapshot created",
    snapshotFailed: "Snapshot failed",
    deploy: "Deploy",
    deployed: "Deployed",
    deployFailed: "Deploy failed",
    undeploy: "Undeploy",
    undeployed: "Undeployed — runs fall back to the live draft",
    undeployFailed: "Undeploy failed",
  },
  captures: {
    title: "Captures",
    subtitle:
      'Replayable trigger fixtures. Enable "capture next fire" on a webhook trigger or watcher to record real payloads here without firing the agent.',
    empty:
      'No captures yet. Enable "capture next fire" on a webhook or watcher, then trigger it once to record the payload.',
    refresh: "Refresh",
    replay: "Replay against an employee",
    replayTitle: "Replay capture",
    replayBody:
      'Re-execute "{name}" against the chosen employee. A new run is created (trigger_kind=replay) and the resulting conversation opens.',
    replayStarted: "Replay started",
    replayFailed: "Replay failed",
    employee: "Employee",
    pickEmployee: "Pick an employee…",
    cancel: "Cancel",
    delete: "Delete",
    deleted: "Capture deleted",
    deleteFailed: "Delete failed",
    loadFailed: "Could not load captures",
    cols: {
      name: "Name",
      source: "Source",
      captured: "Captured",
      actions: "Actions",
    },
  },
  mcpCatalog: {
    title: "MCP integration catalog",
    subtitle:
      "Superadmin: the catalog of MCP integrations that prefill the Custom MCP server tool. Built-in entries are code-owned (re-seed overwrites them); add custom entries per environment.",
    create: "New integration",
    addFromRegistry: "Add from registry",
    empty: "No catalog entries yet.",
    registry: {
      title: "Add from the MCP registry",
      subtitle:
        "Search the official MCP registry and add a server to the catalog. Review and set the category + credential before saving.",
      searchPlaceholder: "Search the MCP registry…",
      search: "Search",
      transport: "Transport:",
      verifiedOnly: "Verified only",
      source:
        "Results come from registry.modelcontextprotocol.io. Trusted = publisher is in your org's allowlist; Community = review before adding.",
      empty: "No registry results for this filter.",
      hidden:
        "{n} more hidden by the transport filter — toggle stdio/sse/http above. Most official servers are published as stdio.",
      refresh: "Refresh cache from registry",
      refreshFailed: "Could not refresh the registry cache",
      searchFailed: "Registry search failed",
      trusted: "Trusted",
      trustedTitle: "Publisher is in your org's trusted allowlist.",
      verified: "Verified",
      verifiedTitle:
        "Verified vendor — the publisher proved DNS control of this domain (e.g. com.notion = Notion's official server).",
      community: "Community",
      communityTitle:
        "GitHub-published (io.github.*) or unverified namespace — review before adding.",
      add: "Add",
      close: "Close",
    },
    builtin: "Built-in",
    builtinShort: "Built-in",
    custom: "Custom",
    secretCred: "vault secret",
    noCred: "none",
    saved: "Integration updated",
    created: "Integration created",
    saveFailed: "Save failed",
    deleted: "Integration deleted",
    deleteFailed: "Delete failed",
    loadFailed: "Could not load the catalog",
    toggleFailed: "Could not change active state",
    cloneTitle: "Clone",
    editTitle: "Edit",
    deleteTitle: "Delete",
    systemReadOnly: "Built-in integration — clone it to customize",
    deleteConfirmTitle: 'Delete "{name}"?',
    deleteConfirmBody:
      "Employees that already installed this integration are unaffected (they keep their saved config). It only disappears from the install picker.",
    cols: {
      name: "Name",
      key: "Key",
      category: "Category",
      transport: "Transport",
      credential: "Credential",
      source: "Source",
      active: "Active",
      actions: "Actions",
    },
    dialog: {
      createTitle: "New integration",
      editTitle: "Edit integration",
      cloneTitle: "Clone integration",
      subtitle:
        "mcpConfig is the MCPServerConfig sent to the connector. Resource/secret specs bind the credential the wizard will ask for.",
    },
    form: {
      key: "Key",
      keyLocked: "The key identifies the entry and can't be changed.",
      name: "Name",
      category: "Category",
      sortOrder: "Sort order",
      description: "Description",
      iconUrl: "Icon URL",
      docsUrl: "Docs URL",
      mcpConfig: "MCP config (JSON)",
      mcpConfigHint:
        'e.g. { "transport": "streamable_http", "url": "https://mcp.tavily.com/mcp/", "auth_query_param_name": "tavilyApiKey", "auth_query_param_field": "api_key" }',
      resourceSpec: "Resource binding (JSON, optional)",
      resourceSpecHint:
        'Bind a typed Resource for the credential, e.g. { "resourceTypeKey": "tavily_api_key", "fieldToEnvVar": {} }. Leave blank for keyless integrations.',
      secretSpec: "Legacy secret spec (JSON, optional)",
      secretSpecHint:
        "Only for stdio integrations using a named vault secret. Leave blank when using a Resource.",
      active: "Active (shown in the install picker)",
      save: "Save",
      cancel: "Cancel",
      missingRequired: "Key and name are required",
      mcpConfigRequired: "MCP config is required",
      invalidJson: "{field}: invalid JSON",
      notAnObject: "{field}: must be a JSON object",
    },
  },
  resources: {
    title: "Resources",
    subtitle:
      "Typed connections (API keys, mailboxes, workspaces). Tools and watchers reference these instead of carrying raw credentials.",
    empty:
      "No resources yet. Create one to give a tool or watcher access to an external system.",
    create: "New resource",
    edit: "Edit resource",
    cancel: "Cancel",
    save: "Save",
    saved: "Resource saved",
    saveFailed: "Save failed",
    deleted: "Resource deleted",
    deleteFailed: "Delete failed",
    loadFailed: "Could not load resources",
    typeLocked:
      "Type: {name} (cannot change after creation — delete + recreate to switch)",
    test: "Test connection",
    testOk: "Connection OK",
    testFailed: "Connection failed",
    testNotAvailable: "No test available",
    form: {
      name: "Name",
      type: "Type",
      pickType: "Pick a type…",
    },
    cols: {
      name: "Name",
      fields: "Fields",
      updated: "Updated",
      actions: "Actions",
    },
    whatsapp: {
      title: "Pair WhatsApp",
      instructions:
        "On your phone, open WhatsApp → Settings → Linked devices → Link a device, then scan the QR code below.",
      polling: "Waiting for scan…",
      paired: "Paired as {jid}.",
      pairedTitle: "Paired",
      close: "Close",
      qrAlt: "WhatsApp pairing QR code",
      error: "Pairing error: {message}",
    },
    whatsappRoutes: {
      openTitle: "Auto-respond routes",
      title: "WhatsApp auto-respond routes",
      subtitle:
        "Bind {name} to one or more AI employees. When an inbound message arrives the bound employee runs and its reply is sent back from this device.",
      newRoute: "Add a new route",
      pickEmployee: "Choose an employee…",
      chatFilterPlaceholder: "Optional chat prefix (e.g. 33612@s.whatsapp.net)",
      add: "Add",
      optInHint:
        "New routes start disabled — toggle them on once the employee is ready to respond.",
      empty: "No routes yet. Add one above to start auto-responding.",
      cols: {
        employee: "Employee",
        chatFilter: "Chat filter",
        enabled: "Enabled",
        actions: "",
      },
      created: "Route created",
      createFailed: "Could not create route",
      updateFailed: "Could not update route",
      deleteFailed: "Could not delete route",
      loadFailed: "Could not load routes",
      close: "Close",
    },
  },
  webhooks: {
    title: "Webhook triggers",
    subtitle:
      "Expose a public URL; each POST fires an agent run. HMAC over the body is the trust boundary.",
    empty:
      "No webhook triggers yet. Create one to give an external system a URL it can call.",
    create: "New webhook trigger",
    copyUrl: "Copy webhook URL",
    loadFailed: "Could not load webhook triggers",
    cols: {
      name: "Name",
      url: "URL",
      signature: "Signature",
      enabled: "Enabled",
      actions: "Actions",
    },
  },
  runs: {
    title: "Runs",
    subtitle:
      "Every execution: scheduled fires, manual triggers, webhook calls, replays.",
    empty:
      "No runs yet. They show up here as soon as a scheduled run fires or you trigger one manually.",
    refresh: "Refresh",
    loadFailed: "Could not load runs",
    scopedToSchedule: "Showing runs for scheduled run:",
    scopedToConversation: "Showing runs for this conversation.",
    clearFilter: "Clear filter",
    filters: {
      status: "Status",
      triggerKind: "Trigger",
      allStatuses: "All statuses",
      allTriggers: "All triggers",
    },
    cols: {
      created: "Created",
      trigger: "Trigger",
      status: "Status",
      duration: "Duration",
      attempt: "Attempt",
      error: "Error",
    },
    detail: {
      notFound: "Run not found.",
      loadFailed: "Could not load run",
      backToEmployee: "Back to employee",
      backToRuns: "Back to runs",
      cancel: "Cancel",
      cancelled: "Run cancelled",
      cancelFailed: "Cancel failed",
      replay: "Replay",
      replayed: "Replay started",
      replayFailed: "Replay failed",
      openConversation: "Open conversation",
      created: "Created",
      started: "Started",
      finished: "Finished",
      duration: "Duration",
      credits: "Credits",
      triggerRef: "Trigger source",
      parentRun: "Parent run",
      idempotencyKey: "Idempotency key",
      nextRetry: "Next retry",
      error: "Error",
      payload: "Payload",
      output: "Output",
    },
  },
  form: {
    name: "Name",
    role: "Role",
    systemPrompt: "System Prompt",
    llmKey: "Model",
    temperature: "Temperature",
    maxSteps: "Max Steps",
    avatarUrl: "Avatar URL",
    isActive: "Active",
    activeOn: "Active",
    activeOff: "Paused",
    activeOnHint:
      "This employee is live — chat, scheduled runs, webhooks, and triggers can all fire.",
    activeOffHint:
      "Paused — chat is disabled and no scheduled runs, webhooks, or triggers will fire until you switch it back on. Config and history are kept.",
    create: "Create Employee",
    edit: "Edit Employee",
  },
  detail: {
    title: "Employee Detail",
    subAgents: "Sub-Agents",
    addSubAgent: "Add Sub-Agent",
    tools: "Tools",
    addTool: "Add Tool",
    removeToolConfirm: {
      title: "Remove this tool?",
      message:
        "The sub-agent will no longer have access to this integration. This action cannot be undone.",
    },
    conversations: "Conversations",
    newConversation: "New Conversation",
    chat: "Chat",
  },
  subagent: {
    name: "Name",
    description: "Description",
    systemPrompt: "System Prompt",
    llmKey: "Model override",
    maxSteps: "Max Steps",
    sortOrder: "Sort Order",
  },
  chart: {
    invalid: "The chart could not be displayed.",
  },
  artifact: {
    notFound: "This artifact could not be found.",
    untitled: "Untitled",
    unsupported: "Unsupported artifact type: {type}",
  },
  conversation: {
    title: "Conversation",
    placeholder: "Type your message...",
    placeholderFor: "Ask {name} anything…",
    send: "Send",
    back: "Back",
    untitled: "Untitled conversation",
    renameHint: "Click to rename",
    renameFailed: "Rename failed",
    sharedReadOnly: "Shared team thread — only admins can post here.",
    welcomeSubtitle:
      "Start a new thread with {name}. Use /commands for ready-made prompts, or just type a question.",
    welcomeNoEmployee: "Loading conversation…",
    typing: "Typing…",
    activity: "Activity",
    activityWaiting: "Waiting for the first step…",
    activityCollapse: "Hide activity panel",
    activityExpand: "Show activity panel",
    delete: "Delete",
    deleteTitle: "Delete thread?",
    deleteMessage:
      "Removes this chat thread and its messages. Run history, cost, and any facts the employee learned are kept.",
    deleted: "Thread deleted",
    deleteFailed: "Delete failed",
    openAuditQueue: "Open audit queue",
    openAuditQueueTitle:
      "Open the care template audit queue to validate or reject findings",
    viewRuns: "Runs",
    viewRunsTitle: "View the runs this conversation produced",
    notice: {
      maxStepsTitle: "I reached my step limit",
      guardrailTitle: "Stopped by a guardrail",
      errorTitle: "Something interrupted this response",
      errorFallback:
        "Something went wrong while generating the response. Please try again.",
      keepGoing: "Keep going",
      rephrase: "Rephrase",
      openRun: "Open run",
      keepGoingPrompt: "Please continue from where you left off.",
    },
    tool: {
      knowledge: "Knowledge base",
      passages: "no passages | {n} passage | {n} passages",
      result: "result",
      sources: "Sources",
      linkCheck: "Link check",
      linkHealthy: "Healthy",
      linkRedirected: "Redirected",
      linkBroken: "Broken",
      linkUnreachable: "Unreachable",
      linkBlocked: "Blocked",
      linkRedirectsTo: "redirects to",
    },
  },
  home: {
    composerPlaceholder: "Ask {name} anything…",
    activeThread: "Active Thread:",
    newThreadActive: "Starting a new thread",
    newThread: "New Thread",
    newSharedThread: "New shared chat",
    sharedThread: "Shared team thread",
    metricsTitle: "Performance metrics (7d)",
    statConversations: "Conversations",
    statToolCalls: "Tool calls",
    statSuccessRate: "Tool success rate",
    careStats: {
      openFindings: "Open findings",
      critical: "Critical (high sev)",
      lastAudit: "Last audit",
    },
    threadContext: "Thread context",
    threadMessages: "Messages",
    threadLastActivity: "Last activity",
    openFullConversation: "Open full conversation",
    recentTitle: "Recent conversations",
    recentCollapse: "Hide recent conversations",
    recentExpand: "Show recent conversations",
    empty: "No conversations yet.",
  },
  // Phase 5 / F18: per-employee tabbed view.
  tabGroups: {
    operate: "Operate",
    configure: "Configure",
    observe: "Observe",
  },
  tabs: {
    chat: "Chat",
    setup: "Setup",
    behavior: "Behavior",
    triggers: "Triggers",
    memory: "Memory",
    runs: "Runs",
    findings: "Findings",
    deliveryLog: "Delivery log",
    changes: "Changes",
    insights: "Insights",
    cost: "Cost",
  },
  cost: {
    title: "Cost",
    subtitle: "AI spend tracked over the last {days} days.",
    totalCost: "Total spend",
    totalTokens: "Total tokens",
    totalCalls: "Model calls",
    avgPerConversation: "Avg / conversation",
    savedTokens: "Tokens saved",
    inputAvoided: "of input avoided",
    summarizedTokens: "Context compacted",
    contextRemoved: "tokens removed from history",
    dailySpend: "Daily spend",
    byModel: "Spend by model",
    modelHeader: "Model",
    callsHeader: "Calls",
    tokensHeader: "Tokens",
    spendHeader: "Spend",
    emptySeries: "No model calls in this period.",
    emptyModels: "No model usage yet.",
    loadFailed: "Could not load cost stats",
    runCredits: "Run credits remaining",
    runCreditsHint: "One credit per completed run. Failed runs aren't charged.",
    unlimited: "Unlimited",
  },
  knowledge: {
    title: "Knowledge base",
    subtitle:
      "Documents your AI employees can search and cite. Tag each document; an employee's knowledge_search tool reads only the tags it's granted.",
    newTitle: "Add document",
    docTitle: "Title",
    docTitlePlaceholder: "e.g. Refund policy 2026",
    tags: "Tags",
    tagsPlaceholder: "Type to search or add a tag…",
    tagsHint:
      "An employee reads a document only if its knowledge_search tool is granted one of these tags.",
    content: "Content",
    contentPlaceholder:
      "Paste plain text or markdown. Headings become section references in citations.",
    ingest: "Add to knowledge base",
    uploadFile: "Upload file",
    uploading: "Uploading…",
    uploadHint:
      "PDF, Word, HTML… — text extracted automatically. Uses the title/tags above.",
    progressExtracting: "Extracting text…",
    progressEmbedding: "Embedding {n} chunks…",
    progressStoring: "Storing {done}/{total}…",
    ingesting: "Indexing…",
    ingested: "Document indexed",
    ingestFailed: "Could not index document",
    colTitle: "Title",
    colTags: "Tags",
    colStatus: "Status",
    colChunks: "Chunks",
    colCreated: "Added",
    empty:
      "No documents yet. Add one above to give your employees something to cite.",
    loadFailed: "Could not load documents",
    deleteConfirm:
      "Delete this document and its chunks? This cannot be undone.",
    deleted: "Document deleted",
    deleteFailed: "Could not delete document",
    editTags: "Edit tags",
    tagsUpdated: "Tags updated",
    tagsUpdateFailed: "Could not update tags",
    cancel: "Cancel",
    save: "Save",
    toolConfig: {
      title: "Knowledge Search",
      subtitle:
        "Choose which documents this employee may read. Untagged documents are readable only when no tags are set here.",
      tags: "Readable tags",
      match: "Match",
      matchAny: "Any",
      matchAll: "All",
      topN: "Max passages",
      floor: "Relevance floor",
      previewUntagged:
        "Reads only untagged documents (the general, unscoped corpus).",
      previewAny: "Reads documents tagged with any of: {tags}.",
      previewAll: "Reads only documents tagged with all of: {tags}.",
      saved: "Knowledge Search configured",
    },
    statusPending: "Pending",
    statusIndexed: "Indexed",
    statusFailed: "Failed",
    sourcesCited: "{n} source(s) retrieved",
    noSources: "No matching sources",
    untitledSection: "Untitled section",
    evalTitle: "Evaluate retrieval",
    evalSubtitle:
      "Measure hit-rate before tuning the retriever. One case per line: query {'|'} expected terms (optional — without them the case just measures retrieval). Separate terms with commas to require all of them (e.g. Vue.js, Golang); terms match the passage text and the document title (the candidate's name). Scope by the same tags an employee's tool would have.",
    evalTags: "Tags",
    evalFloor: "Similarity floor",
    evalFloorHint: "0.3 default; set 0 to measure raw recall.",
    evalCases: "Cases",
    evalCasesPlaceholder:
      "qui a cette expérience ? {'|'} Architecte et innovateur technologique\nquelles technologies utilise-t-il ?",
    evalRun: "Run evaluation",
    evalRunning: "Evaluating…",
    evalFailed: "Evaluation failed",
    evalHitRate: "Hit rate",
    evalAvgScore: "Avg top score",
    evalColQuery: "Query",
    evalColHit: "Hit",
    evalColRank: "Rank",
    evalColScore: "Top score",
    evalUnlabeled:
      'No expected phrase for this case — add "| phrase" to score a hit. Retrieval still ran (see Top score).',
  },
  auditExport: {
    title: "Audit export",
    subtitle:
      "Download the tenant's delivery-event audit trail — every external action your AI employees took (emails/Slack sent, watcher polls, resource resolves), with actor, target, outcome and timestamp — for a compliance or security review.",
    range: "Date range",
    allTime: "All time",
    hint: "Leave the range empty to export everything. Capped at 50,000 events.",
    download: "Download JSON",
    downloading: "Exporting…",
    exported: "Exported {n} event(s).",
    failed: "Export failed",
  },
  changesetsTab: {
    title: "ChangeSets for {name}",
    subtitle:
      "Every Architect proposal that targets this employee — drafts awaiting review, applied changes, rejections, and rollbacks. Click a row to see the before/after diff.",
    empty: "No ChangeSets yet for {name}.",
    columns: {
      id: "ID",
      status: "Status",
      proposedBy: "Proposed by",
      createdAt: "Proposed",
      appliedAt: "Applied",
      ops: "Ops",
      rationale: "Rationale",
    },
  },
  deliveryLog: {
    title: "Delivery log",
    subtitle:
      "Every approval send, watcher poll, and credential resolve for this employee. Use this to answer 'did my Slack approval go out?' without grepping backend logs.",
    empty: "No delivery events yet.",
    refresh: "Refresh",
    loadFailed: "Could not load delivery events",
    filter: {
      all: "All",
      ok: "Successful only",
      failed: "Failed only",
    },
    cols: {
      when: "When",
      kind: "Kind",
      target: "Target",
      status: "Status",
      latency: "Latency",
    },
  },
  triggersTab: {
    title: "Triggers for {name}",
    subtitle: "Webhooks, schedules, and captures scoped to this employee.",
    webhooks: "Webhook triggers",
    schedules: "Scheduled runs",
    captures: "Captures",
    seeAll: "See all",
    none: "No {kind} for this employee yet.",
    newWebhook: "New webhook",
    newSchedule: "New schedule",
    createFirstWebhook: "Create first webhook",
    createFirstSchedule: "Create first schedule",
    webhookEmptyHint:
      "Let external services (Slack events, GitHub, Stripe…) trigger this employee.",
    scheduleEmptyHint:
      "Run this employee on a cron — daily digests, polling, periodic syncs.",
    captureEmptyHint:
      'Captures are created by toggling "Capture next fire" on a webhook or scheduled run. Use them to replay real traffic during dry-runs.',
  },
  runsTab: {
    title: "Recent runs for {name}",
    subtitle: "Latest executions across all triggers for this employee.",
    empty: "No runs yet. They appear here once {name} is triggered.",
    seeAll: "See all runs",
  },
  insights: {
    title: "Insights for {name}",
    subtitle:
      "How this employee performed lately — usage, costs and health signals.",
    findingsQueue: "Findings queue →",
    findingsQueueHint:
      "Open the care template audit queue to validate or reject findings.",
    activityTitle: "Activity · last {days} days",
    activityEmpty:
      "No tool activity yet. Stats appear here once this employee starts using tools.",
    topTools: "Top tools used",
    errorGeneric: "A recent call failed.",
    viewFailingRun: "View failing run →",
  },
  approvalDelivery: {
    sectionTitle: "Integrations & output",
    label: "Approval delivery",
    caption:
      "Where this employee's approval requests are sent. Pick one channel — exactly one fires per approval.",
    modes: {
      email: {
        label: "Email",
        hint: "Send to a custom address",
      },
      slack: {
        label: "Slack",
        hint: "Post to a channel via your bot",
      },
    },
    email: {
      recipientLabel: "Recipient email",
      helper:
        "Address that receives the single-use approval link. Use a shared inbox if multiple people approve.",
      missingWarning:
        "No recipient set — approval emails will fail to send until you add one.",
    },
    slack: {
      workspaceLabel: "Slack workspace",
      workspacePlaceholder: "Select a workspace…",
    },
    errors: {
      emailRequired: "Pick an email recipient before saving.",
      emailInvalid: "That doesn't look like a valid email address.",
      slackWorkspaceRequired: "Pick a Slack workspace before saving.",
      slackChannelRequired: "Pick a Slack channel before saving.",
    },
  },
  integrations: {
    nativeBadge: "Native",
    nativeAdded: "Tool added",
    nativeAddFailed: "Could not add tool",
    nativeNeedsConfig: "Added — configure credentials on the Behavior tab.",
  },
};
